import {
  AwsFormFields,
  AwsMarketplaceType,
  AzureActivateSubscriptionPayload,
  Cloud,
  countriesKeyedByIsoCode,
  Edition,
  OidcSignupCompleteRequest,
  OidcSignupCompleteResponse,
  OidcSignupStartRequest,
  OidcSignupStartResponse,
  OidcVerifyIdentityRequest,
  OidcVerifyIdentityResponse,
  RawSignupFields,
  RawWaitlistFields,
  SignupFlowType,
  SignupRequestResponse,
} from '@signup/shared';
import { MarketingParams, SignupParams } from '../utils/ParseQueryParams';
import { FullEvent } from '@snowflake/core-ui';
import { getSignupFlowTypeWithoutListener } from '../utils/configurations';
import { MutinyExperience, MutinyExperiencesSummary } from '../utils/MutinyExperience';

type SupportedRequestInit = Pick<RequestInit, 'method' | 'body'> & {
  headers: Record<string, string> | undefined;
};

function injectHeaders(
  headers: Record<string, string> | undefined,
): Record<string, string> | undefined {
  const mutinyData = MutinyExperience.getMutinyExperienceData();

  // Select primary experience - prefer non-control experiences
  const primaryExperience =
    mutinyData.experiences.find(exp => exp.impressionType !== 'control') ||
    mutinyData.experiences[0];

  const injectedHeaders = {
    'signup-flow-type': getSignupFlowTypeWithoutListener(),
    // Add Mutiny headers for multiple experiences
    'x-mutiny-experience-count': mutinyData.experienceCount.toString(),
    ...(mutinyData.experienceCount > 0 && {
      'x-mutiny-experiences': encodeURIComponent(
        JSON.stringify(
          mutinyData.experiences.map(exp => ({
            audienceSegment: exp.audienceSegment,
            experience: exp.experience,
            impressionType: exp.impressionType,
            page: exp.page,
            variationKey: exp.variationKey,
            variationName: exp.variationName,
          })),
        ),
      ),
      // Include primary experience headers for backwards compatibility
      ...(primaryExperience &&
        primaryExperience.experience &&
        primaryExperience.variationKey && {
          'x-mutiny-primary-experience': encodeURIComponent(primaryExperience.experience),
          'x-mutiny-primary-variation-key': primaryExperience.variationKey,
          'x-mutiny-primary-impression-type': primaryExperience.impressionType || '',
        }),
    }),
  };

  if (!headers) {
    return injectedHeaders;
  }

  return {
    ...headers,
    ...injectedHeaders,
  };
}

async function fetchSignupApi(url: string, init: SupportedRequestInit): Promise<Response> {
  return fetch(url, {
    ...init,
    headers: injectHeaders(init.headers),
  });
}

export class SignupApi {
  static prepareSignupFields({
    firstName,
    lastName,
    email,
    company,
    phoneNumber,
    role,
    edition,
    cloud,
    region,
    awsType,
    signupParams,
    formId,
    countryIsoCode,
    recaptchaToken,
    optOutEmailAgreement,
    givenEmailOptInChoice,
    marketingParams,
    formTwoCompletionTime,
    optInEmailAgreement,
    mutinyExperienceData,
    radarSession,
  }: {
    firstName: string;
    lastName: string;
    email: string;
    company: string;
    role: string;
    edition: Edition;
    phoneNumber?: string;
    cloud: Cloud;
    region: string;
    awsType?: AwsMarketplaceType;
    signupParams: SignupParams;
    formId: string;
    countryIsoCode: string;
    recaptchaToken: string;
    optOutEmailAgreement?: boolean;
    givenEmailOptInChoice?: boolean;
    marketingParams: MarketingParams;
    formTwoCompletionTime: number;
    optInEmailAgreement: boolean;
    mutinyExperienceData?: MutinyExperiencesSummary;
    radarSession?: string;
  }): RawSignupFields {
    return {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim().toLowerCase(),
      company: company.trim(),
      phoneNumber,
      role: role,
      edition,
      cloud: cloud, // we know its going to be set here cause of validation
      region: region, // we know its going to be set here cause of validation
      country: countriesKeyedByIsoCode[countryIsoCode].value,
      recaptchaToken,
      ...(awsType !== undefined &&
        signupParams.ampt !== undefined && {
          ampt: signupParams.ampt,
          awsType: awsType,
        }),
      ...(optOutEmailAgreement !== undefined && { optOutEmailAgreement }), // should only be undefined for 'United States'
      ...(givenEmailOptInChoice && { givenEmailOptInChoice, optInEmailAgreement }),
      ...(signupParams.owner && { owner: signupParams.owner }),
      ...(signupParams.eng && { eng: signupParams.eng }),
      ...(signupParams.sdm && { sdm: signupParams.sdm }),
      ...(getSignupFlowTypeWithoutListener() === SignupFlowType.Developer
        ? { developers: true }
        : {}),
      ...(signupParams.listing && { listing: signupParams.listing }),
      ...(signupParams.lab && { lab: signupParams.lab }),
      ...(signupParams.referrer && { referrer: signupParams.referrer }),
      ...(signupParams.abexperiment && { abexperiment: signupParams.abexperiment }),
      ...(signupParams.account && { account: signupParams.account }),
      ...(signupParams.template && { template: signupParams.template }),
      ...(signupParams.workspaces && { workspaces: signupParams.workspaces }),
      ...(signupParams.E6CCEE4CEB && { E6CCEE4CEB: signupParams.E6CCEE4CEB }),
      ...(signupParams.token && { token: signupParams.token }),
      ...(signupParams.k && { k: signupParams.k }),
      ...(signupParams.t && { t: signupParams.t }),
      ...(signupParams.program && { program: signupParams.program }),
      ...marketingParams,
      signupUrl: window.location.href,
      formId: formId,
      formTwoCompletionTime,
      ...(radarSession && { radarSession }),
      // Add Mutiny experience data for multiple experiences
      ...(mutinyExperienceData &&
        mutinyExperienceData.experienceCount > 0 && {
          mutinyExperienceCount: mutinyExperienceData.experienceCount,
          mutinyExperiences: JSON.stringify(
            mutinyExperienceData.experiences.map(exp => ({
              audienceSegment: exp.audienceSegment,
              experience: exp.experience,
              impressionType: exp.impressionType,
              page: exp.page,
              variationKey: exp.variationKey,
              variationName: exp.variationName,
            })),
          ),
          // Include primary experience fields for backwards compatibility
          // Select primary experience - prefer non-control experiences
          ...(() => {
            const primaryExp =
              mutinyExperienceData.experiences.find(exp => exp.impressionType !== 'control') ||
              mutinyExperienceData.experiences[0];
            return primaryExp && primaryExp.experience && primaryExp.variationKey
              ? {
                  mutinyPrimaryExperience: primaryExp.experience,
                  mutinyPrimaryVariationKey: primaryExp.variationKey,
                  mutinyPrimaryVariationName: primaryExp.variationName || '',
                  mutinyPrimaryImpressionType: primaryExp.impressionType || '',
                  mutinyPrimaryAudienceSegment: primaryExp.audienceSegment || '',
                }
              : {};
          })(),
        }),
    };
  }

  static async createTrial(options: RawSignupFields): Promise<{
    res: SignupRequestResponse;
    headers: Headers;
  }> {
    const rawRes = await fetchSignupApi('/api/v1/createtrial', {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(options),
    });
    return {
      headers: rawRes.headers,
      res: await rawRes.json(),
    };
  }

  static async joinWaitlist(options: RawWaitlistFields): Promise<{
    res: SignupRequestResponse;
    headers: Headers;
  }> {
    const rawRes = await fetchSignupApi('/api/v1/joinwaitlist', {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(options),
    });
    return {
      headers: rawRes.headers,
      res: await rawRes.json(),
    };
  }

  static async addAwsSubscription(fields: AwsFormFields): Promise<Response> {
    return fetchSignupApi('/api/v1/aws_add_subscription', {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(fields),
    });
  }

  static async activateAzureSubscription(
    fields: AzureActivateSubscriptionPayload,
  ): Promise<Response> {
    return fetchSignupApi('/azure/activate_subscription', {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(fields),
    });
  }

  static async resolveAzurePurchaseToken(token: string | string[]): Promise<Response> {
    return fetchSignupApi('/azure/resolvepurchasetoken', {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token }),
    });
  }

  static async getListingInformation(listingId: string): Promise<Response> {
    return fetchSignupApi(`/api/v1/listing/${listingId}`, {
      method: 'get',
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  static async getDeploymentMetadataByListingGlobalId(listingGlobalId: string): Promise<Response> {
    return fetchSignupApi(`/api/v1/listing-available-regions/${listingGlobalId}`, {
      method: 'get',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });
  }

  static async startOidcSignup(options: OidcSignupStartRequest): Promise<OidcSignupStartResponse> {
    const rawRes = await fetchSignupApi('/api/v1/start-oidc-signup', {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(options),
    });
    if (!rawRes.ok) {
      throw new Error(`startOidcSignup failed: ${rawRes.status}`);
    }
    return rawRes.json();
  }

  static async verifyOidcIdentity(
    options: OidcVerifyIdentityRequest,
  ): Promise<OidcVerifyIdentityResponse> {
    const rawRes = await fetchSignupApi('/api/v1/verify-oidc-identity', {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(options),
    });
    if (!rawRes.ok) {
      let code: string | undefined;
      try {
        const body = await rawRes.json();
        code = typeof body?.error === 'string' ? body.error : undefined;
      } catch {
        // body not JSON — leave code undefined
      }
      throw new OidcSignupError(rawRes.status, code, 'verifyOidcIdentity');
    }
    return rawRes.json();
  }

  static async completeOidcSignup(
    options: OidcSignupCompleteRequest,
  ): Promise<OidcSignupCompleteResponse> {
    const rawRes = await fetchSignupApi('/api/v1/complete-oidc-signup', {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(options),
    });
    if (!rawRes.ok) {
      let code: string | undefined;
      try {
        const body = await rawRes.json();
        code = typeof body?.error === 'string' ? body.error : undefined;
      } catch {
        // body not JSON — leave code undefined
      }
      throw new OidcSignupError(rawRes.status, code, 'completeOidcSignup');
    }
    return rawRes.json();
  }

  static async sendTelemetry(events: FullEvent[]): Promise<Response> {
    return fetchSignupApi('/api/v1/telemetry', {
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(events),
    });
  }

  // https://developer.mozilla.org/en-US/docs/Web/API/Beacon_API/Using_the_Beacon_API
  static async sendTelemetryWithBeacon(events: FullEvent[]): Promise<void> {
    // Beacon API doesn't support passing headers: everything of the original request is marshaled into the request payload.
    const payload = {
      body: events,
      headers: injectHeaders(undefined),
    };

    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    navigator.sendBeacon('/api/v1/telemetry_beacon', blob);
  }
}

// Thrown by SignupApi.verifyOidcIdentity / completeOidcSignup when the server returns a non-2xx.
// Network failures still throw the raw fetch error — callers use
// `err instanceof OidcSignupError` to distinguish server-rejected (non-recoverable)
// from transport failures (retryable in-form).
export class OidcSignupError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string | undefined,
    public readonly endpoint: 'verifyOidcIdentity' | 'completeOidcSignup',
  ) {
    super(`${endpoint} failed: ${status}${code ? ` (${code})` : ''}`);
    this.name = 'OidcSignupError';
  }
}
